package com.example.crashbit.data

import com.example.crashbit.Nebula
import com.example.crashbit.SpaceBody
import com.example.crashbit.WorldState
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import kotlinx.coroutines.flow.Flow

data class UniverseModel(
    val id: Long = 0,
    val name: String,
    val mode: String = "CUSTOM",
    val createdAt: Long = 0L,
    val timestamp: Long = 0L,
    val scale: Float = 1f,
    val panX: Float = 0f,
    val panY: Float = 0f,
    val isCrazyEddie: Boolean = false,
    val starsCanMerge: Boolean = true,
    val starsAreDynamic: Boolean = false,
    val planetsCanBeConsumed: Boolean = false,
    val auLimit: Float = 10000f,
    val isPaused: Boolean = false,
    val useGpu: Boolean = false
)

data class CelestialBodyModel(
    val id: Long,
    val universeId: Long,
    val name: String,
    val posX: Float,
    val posY: Float,
    val velX: Float,
    val velY: Float,
    val mass: Float,
    val radius: Float,
    val color: Int,
    val isStar: Boolean,
    val isBlackHole: Boolean,
    val hasRings: Boolean,
    val temperature: Float,
    val rotation: Float,
    val rotationSpeed: Float,
    val lifeProgress: Float,
    val alienProgress: Float,
    val hasLife: Boolean,
    val hasAliens: Boolean,
    val minObservedDist: Float,
    val maxObservedDist: Float,
    val compositionJson: String,
    val closePassJson: String,
    val overlappingJson: String,
    val overlapFrameJson: String
)

data class NebulaModel(
    val id: Long,
    val universeId: Long,
    val name: String,
    val posX: Float,
    val posY: Float,
    val radius: Float,
    val density: Float,
    val color: Int,
    val compositionJson: String
)

data class UniverseWithContentsModel(
    val universe: UniverseModel,
    val bodies: List<CelestialBodyModel>,
    val nebulae: List<NebulaModel>
)

fun CelestialBodyModel.toModel() = SpaceBody(
    id = id, name = name, position = Offset(posX, posY), velocity = Offset(velX, velY),
    mass = mass, radius = radius, color = Color(color), isStar = isStar, isBlackHole = isBlackHole,
    hasRings = hasRings, temperature = temperature, rotation = rotation, rotationSpeed = rotationSpeed,
    lifeProgress = lifeProgress, alienProgress = alienProgress, hasLife = hasLife, hasAliens = hasAliens,
    jordyPopulation = 0L, parentIds = emptyList(), minObservedDist = minObservedDist, maxObservedDist = maxObservedDist
)

fun NebulaModel.toModel() = Nebula(
    id = id, name = name, position = Offset(posX, posY), radius = radius, density = density, color = Color(color), composition = emptyMap()
)

interface PreferenceManager {
    val showPerformance: Flow<Boolean>
    val starsCanMerge: Flow<Boolean>
    val starsAreDynamic: Flow<Boolean>
    val planetsCanBeConsumed: Flow<Boolean>
    val auLimit: Flow<Float>
    val mergerPassLimit: Flow<Int>
    val evolutionSpeed: Flow<Float>
    val rewindEnabled: Flow<Boolean>
    val deterministicMode: Flow<Boolean>
    val vibrationEnabled: Flow<Boolean>
    val hapticIntensity: Flow<Float>
    val isCloudBackupEnabled: Flow<Boolean>
    val hasSeenOnboarding: Flow<Boolean>
    val languageCode: Flow<String>

    suspend fun setShowPerformance(v: Boolean)
    suspend fun setStarsCanMerge(v: Boolean)
    suspend fun setStarsAreDynamic(v: Boolean)
    suspend fun setPlanetsCanBeConsumed(v: Boolean)
    suspend fun setAuLimit(v: Float)
    suspend fun setMergerPassLimit(v: Int)
    suspend fun setEvolutionSpeed(v: Float)
    suspend fun setRewindEnabled(v: Boolean)
    suspend fun setDeterministicMode(v: Boolean)
    suspend fun setVibrationEnabled(v: Boolean)
    suspend fun setHapticIntensity(v: Float)
    suspend fun setCloudBackupEnabled(v: Boolean)
    suspend fun setHasSeenOnboarding(v: Boolean)
    suspend fun setLanguageCode(code: String)
}

interface UniverseRepository {
    val allUniverses: Flow<List<UniverseWithContentsModel>>
    suspend fun saveUniverse(name: String, state: WorldState)
    suspend fun deleteUniverse(universe: UniverseModel)
}
